import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ExternalLink } from 'lucide-react';
import nurseryService from '../../../services/nursery';
import type { CustomerMatchQuery, CustomerMatchReason, NurseryCustomerMatch } from '../../../types/nursery';

/**
 * "This may already be a customer" on the customer form (Rob, 2026-10-20:
 * Raywood Nursery was entered twice). As the name, email, phone, ABN or an
 * address is typed, the server lists customers that match on any of them
 * (HHNodeServer utils/customerMatch.js says how), inactive ones included.
 * It only warns: a store shares its head office's phone, and two customers
 * can share a name. Each opens in a new tab so what is typed here is kept.
 */

const REASON_WORDS: Record<CustomerMatchReason, string> = {
  name: 'name',
  email: 'email',
  phone: 'phone',
  abn: 'ABN',
  address: 'address',
};

const DEBOUNCE_MS = 600;

function reasonsLine(reasons: CustomerMatchReason[]): string {
  const words = reasons.map((r) => REASON_WORDS[r]);
  const list = words.length > 1 ? `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}` : words[0];
  return `Same ${list}`;
}

export default function CustomerMatches({ query }: { query: CustomerMatchQuery }) {
  const [matches, setMatches] = useState<NurseryCustomerMatch[]>([]);
  // The query as text, so a re-render with the same values asks nothing.
  const key = JSON.stringify(query);

  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(() => {
      nurseryService.findCustomerMatches(JSON.parse(key) as CustomerMatchQuery)
        .then((res) => { if (!cancelled) setMatches(res.success ? res.data || [] : []); })
        .catch(() => { /* a server without the check, or a dropped request: the form works without it */ });
    }, DEBOUNCE_MS);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [key]);

  if (!matches.length) return null;
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-3 text-sm text-amber-900 space-y-2" role="status">
      <p className="flex items-start gap-2 font-medium">
        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
        <span className="min-w-0 break-words">
          {matches.length === 1 ? 'This may already be a customer' : `This may already be one of ${matches.length} customers`}
        </span>
      </p>
      <ul className="space-y-1.5 pl-6">
        {matches.map((m) => (
          <li key={m.id} className="min-w-0 break-words">
            <Link to={`/accounts/customers/${m.id}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-medium text-primary-700 hover:underline">
              {m.name}{m.customer_code ? ` (#${m.customer_code})` : ''}
              <ExternalLink className="h-3 w-3 shrink-0" />
            </Link>
            {!m.active && <span className="ml-1.5 text-xs rounded-full bg-neutral-200 text-neutral-700 px-1.5 py-0.5">Inactive</span>}
            <span className="block text-xs text-amber-800">
              {reasonsLine(m.reasons)}
              {m.parent_name ? ` · Site of ${m.parent_name}` : ''}
              {m.delivery_address ? ` · ${m.delivery_address}` : ''}
            </span>
          </li>
        ))}
      </ul>
      <p className="text-xs text-amber-800 pl-6">
        If it is the same customer, use that one rather than adding it again. An inactive one can be made active from its page.
      </p>
    </div>
  );
}
